// load datadog RUM early on to track the app load time
import 'utils/dataDog';

import React from 'react';
import ReactDOM from 'react-dom';
import { Provider, useSelector } from 'react-redux';
import { Route, Redirect } from 'react-router-dom';
import { ConnectedRouter } from 'connected-react-router';
import store from './store';
import App from './containers/App';
import EmailVerificationStep from './containers/EmailVerificationStep';
import Login from './containers/Login';
import LoginVerify from './containers/LoginVerify';
import ForgotPassword from './containers/ForgotPassword';
import ResetPassword from './containers/ResetPassword';
import LoginAuthorize from './containers/LoginAuthorize';
import DeveloperSignUp from './containers/DeveloperSignUp';
import ReviewerSignUpRoute from './containers/ReviewerSignUpRoute';
import LandingPage from './containers/SecurityFixReview/LandingPage';
import ReviewerRoute from '@containers/ReviewerRoute';
import CompleteDeveloperProfile from './containers/CompleteDeveloperProfile';
import Modal from 'react-modal';
import history from './history';
import { MuiTheme, InternetStatus } from 'Common';
import { MuiDarkTheme } from 'Common/theme';
import { Div } from 'Common/components/Atoms/Div';
import ErrorBoundary from './containers/ErrorBoundary';
import {
  ThemeProvider as MuiThemeProvider,
  StyledEngineProvider,
} from '@mui/material/styles';
import StylesProvider from '@mui/styles/StylesProvider';
import DisplayText from 'DisplayText';
import { appThemeSelector } from './selectors/Theme';
import useMediaQuery from '@mui/material/useMediaQuery';

// Import styles and fonts
import './index.css';

const AppIndex = () => {
  return (
    <Provider store={store}>
      <ConnectedRouter history={history}>
        <InnerApp />
      </ConnectedRouter>
    </Provider>
  );
};

const InnerApp = () => {
  const currentTheme = useSelector(appThemeSelector);
  // Based on OS preference
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');

  const getTheme = () => {
    switch (currentTheme) {
      case 'dark':
        return MuiDarkTheme;
      case 'original':
        return MuiTheme;
      default:
        return prefersDarkMode ? MuiDarkTheme : MuiTheme;
    }
  };

  return (
    <StylesProvider injectFirst>
      <StyledEngineProvider injectFirst>
        <MuiThemeProvider theme={getTheme()}>
          <ErrorBoundary>
            <Div
              backgroundColor={getTheme().palette.background.default}
              height="100vh"
              overflow="auto"
            >
              <Route
                path="/"
                exact={true}
                render={(props) => <Redirect to="/dash" />}
              />
              <Route path="/dash" component={App} />
              <Route path="/login" exact component={Login} />
              <Route path="/login/verify" exact component={LoginVerify} />
              <Route path="/login/authorize" exact component={LoginAuthorize} />
              <Route path="/forgot-password" exact component={ForgotPassword} />
              <Route path="/password-reset" exact component={ResetPassword} />
              <Route path="/signup" exact component={DeveloperSignUp} />
              <Route path="/signups/reviewer" component={ReviewerSignUpRoute} />
              <Route path="/signups/security" exact component={LandingPage} />
              <Route path="/reviewer" component={ReviewerRoute} />
              <Route
                path="/verification"
                exact
                component={EmailVerificationStep}
              />
              <Route
                path="/complete_profile"
                exact
                component={CompleteDeveloperProfile}
              />
              <InternetStatus
                onlineMessage={DisplayText.APP.INTERNET_ON}
                offlineMessage={DisplayText.APP.INTERNET_OFF}
              />
            </Div>
          </ErrorBoundary>
        </MuiThemeProvider>
      </StyledEngineProvider>
    </StylesProvider>
  );
};

ReactDOM.render(<AppIndex />, document.getElementById('root'));

Modal.setAppElement('#root');
